import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ExternalLink, Music, Pencil, Plus, Save, X, CheckCircle2 } from "lucide-react";

const PLATFORMS = [
  { key: "muso_ai_url",    label: "Muso.AI",     color: "#7C3AED", hint: "muso.ai/profile/your-handle",      priority: true },
  { key: "genius_url",     label: "Genius",      color: "#FFFF64", hint: "genius.com/artists/your-name",     priority: true },
  { key: "allmusic_url",   label: "AllMusic",    color: "#3B82F6", hint: "allmusic.com/artist/your-name",    priority: true },
  { key: "discogs_url",    label: "Discogs",     color: "#FFFFFF", hint: "discogs.com/artist/...",           priority: false },
  { key: "jaxsta_url",     label: "Jaxsta",      color: "#22D3EE", hint: "jaxsta.com/profile/...",           priority: false },
  { key: "musicbrainz_url",label: "MusicBrainz", color: "#BA478F", hint: "musicbrainz.org/artist/...",       priority: false },
  { key: "imdb_url",       label: "IMDb",        color: "#F5C518", hint: "imdb.com/name/...",                priority: false },
  { key: "soundcloud_url", label: "SoundCloud",  color: "#FF5500", hint: "soundcloud.com/your-name",         priority: false },
  { key: "spotify_url",    label: "Spotify",     color: "#1DB954", hint: "open.spotify.com/artist/...",      priority: false },
];

export default function DiscographyCard() {
  const { user, setUser, refresh } = useAuth();
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const links = user?.discography_links || {};
  const filledCount = useMemo(
    () => PLATFORMS.filter((p) => links[p.key]).length,
    [links],
  );

  useEffect(() => { setShowMore(PLATFORMS.some((p) => !p.priority && links[p.key])); }, [user]);

  if (!user) return null;

  const startEdit = (key) => {
    setEditingKey(key);
    setDraft(links[key] || "");
  };
  const cancel = () => { setEditingKey(null); setDraft(""); };

  const save = async (key, value) => {
    setBusy(true);
    try {
      const { data } = await api.post("/profile", { [key]: value });
      setUser?.(data.user);
      toast.success(value ? "Link saved." : "Link removed.");
      setEditingKey(null);
      setDraft("");
      await refresh?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const visible = showMore ? PLATFORMS : PLATFORMS.filter((p) => p.priority || links[p.key]);

  return (
    <div data-testid="discography-card" className="border border-white/5 bg-[#0c0c0c] p-7 mb-px">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Music size={16} className="text-amber-500" />
            <div className="text-overline">Discography &amp; Credits</div>
          </div>
          <p className="text-sm text-neutral-400 max-w-2xl">
            Paste links to where your producer credits are listed publicly. These appear on your BeatCredsi Passport as proof buttons — visitors can verify your discography on the source platform.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-3xl font-medium text-amber-500" data-testid="discog-count">{filledCount}</div>
          <div className="font-mono-tech text-xs uppercase tracking-widest text-neutral-500">linked</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-5">
        {visible.map((p) => {
          const value = links[p.key] || "";
          const isEditing = editingKey === p.key;
          return (
            <div key={p.key}
              data-testid={`discog-row-${p.key}`}
              className="flex items-center gap-3 px-3 py-2.5 bg-[#0A0A0A] border border-white/5 hover:border-white/10 transition-colors">
              <span className="w-2 h-2 shrink-0" style={{ background: p.color }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-white">{p.label}</span>
                  {value && !isEditing && (
                    <CheckCircle2 size={12} className="text-emerald-400 shrink-0" />
                  )}
                </div>
                {isEditing ? (
                  <input
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") save(p.key, draft.trim());
                      if (e.key === "Escape") cancel();
                    }}
                    placeholder={p.hint}
                    data-testid={`discog-input-${p.key}`}
                    className="w-full mt-1 bg-[#161616] border border-amber-500/40 px-2 py-1.5 text-xs text-white outline-none focus:border-amber-500" />
                ) : value ? (
                  <a href={value} target="_blank" rel="noopener noreferrer"
                    data-testid={`discog-view-${p.key}`}
                    className="font-mono-tech text-xs text-neutral-500 truncate block hover:text-amber-500 transition-colors">
                    {value.replace(/^https?:\/\//, "")}
                  </a>
                ) : (
                  <div className="font-mono-tech text-xs text-neutral-600 truncate">{p.hint}</div>
                )}
              </div>
              {isEditing ? (
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => save(p.key, draft.trim())} disabled={busy}
                    data-testid={`discog-save-${p.key}`}
                    className="p-1.5 bg-amber-500 text-black hover:brightness-110 disabled:opacity-40">
                    <Save size={12} />
                  </button>
                  <button onClick={cancel} data-testid={`discog-cancel-${p.key}`}
                    className="p-1.5 border border-white/15 text-neutral-300 hover:border-rose-400 hover:text-rose-400">
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1 shrink-0">
                  {value && (
                    <a href={value} target="_blank" rel="noopener noreferrer"
                      data-testid={`discog-open-${p.key}`}
                      className="p-1.5 border border-white/15 text-neutral-300 hover:border-amber-500 hover:text-amber-500">
                      <ExternalLink size={12} />
                    </a>
                  )}
                  <button onClick={() => startEdit(p.key)}
                    data-testid={`discog-edit-${p.key}`}
                    className="p-1.5 border border-white/15 text-neutral-300 hover:border-amber-500 hover:text-amber-500">
                    {value ? <Pencil size={12} /> : <Plus size={12} />}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!showMore && PLATFORMS.some((p) => !p.priority && !links[p.key]) && (
        <button onClick={() => setShowMore(true)} data-testid="discog-show-more"
          className="mt-4 text-xs text-amber-500 hover:underline font-mono-tech uppercase tracking-widest">
          + More platforms (Discogs, Jaxsta, MusicBrainz, IMDb, SoundCloud, Spotify)
        </button>
      )}
      {showMore && (
        <button onClick={() => setShowMore(false)} data-testid="discog-show-less"
          className="mt-4 text-xs text-neutral-500 hover:text-white font-mono-tech uppercase tracking-widest">
          − Show fewer
        </button>
      )}

      {user.username && filledCount === 0 && (
        <div className="mt-5 p-3 bg-amber-500/5 border border-amber-500/20 text-xs text-amber-300/90" data-testid="discog-empty-tip">
          Tip: Start with <span className="font-semibold">Muso.AI</span> — it's the producer industry's de-facto credits registry. Add a Genius or AllMusic link too for cross-platform proof.
        </div>
      )}
    </div>
  );
}
